import { useState, useEffect, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Spinner,
  Link,
  IconButton,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { glassCard } from '../styles'
import { apiFetch } from '../utils/apiFetch'

interface Doc {
  _id: string
  title: string
  url: string
  description?: string
  createdAt: string
}

function toEmbedUrl(url: string): string {
  const base = url.split('?')[0].split('#')[0].replace(/\/edit$/, '')
  return `${base}?embedded=true`
}

export default function Documents() {
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const [user] = useState(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })
  const isAdmin = user?.isAdmin === true

  const [docs, setDocs] = useState<Doc[]>([])
  const [loading, setLoading] = useState(true)
  const [viewingDoc, setViewingDoc] = useState<Doc | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Add / edit form
  const [showForm, setShowForm] = useState(false)
  const [editingDoc, setEditingDoc] = useState<Doc | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [formUrl, setFormUrl] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    apiFetch('/api/documents')
      .then(res => res.json())
      .then((data: Doc[]) => { setDocs(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  function openAdd() {
    setEditingDoc(null)
    setFormTitle('')
    setFormUrl('')
    setFormDescription('')
    setShowForm(true)
    setTimeout(() => titleRef.current?.focus(), 50)
  }

  function openEdit(doc: Doc) {
    setEditingDoc(doc)
    setFormTitle(doc.title)
    setFormUrl(doc.url)
    setFormDescription(doc.description ?? '')
    setShowForm(true)
    setTimeout(() => titleRef.current?.focus(), 50)
  }

  function cancelForm() {
    setShowForm(false)
    setEditingDoc(null)
  }

  async function save() {
    if (!formTitle.trim() || !formUrl.trim()) return
    setSaving(true)
    try {
      const body = JSON.stringify({ title: formTitle, url: formUrl, description: formDescription })
      const headers = { 'Content-Type': 'application/json' }
      if (editingDoc) {
        const res = await apiFetch(`/api/documents/${editingDoc._id}`, { method: 'PATCH', headers, body })
        if (res.ok) {
          const updated: Doc = await res.json()
          setDocs(prev => prev.map(d => d._id === editingDoc._id ? updated : d))
          cancelForm()
        }
      } else {
        const res = await apiFetch('/api/documents', { method: 'POST', headers, body })
        if (res.ok) {
          const created: Doc = await res.json()
          setDocs(prev => [created, ...prev])
          cancelForm()
        }
      }
    } finally {
      setSaving(false)
    }
  }

  function confirmDelete(id: string) {
    setDeletingId(id)
    onDeleteOpen()
  }

  async function deleteDoc() {
    if (!deletingId) return
    const res = await apiFetch(`/api/documents/${deletingId}`, { method: 'DELETE' })
    if (!res.ok) { onDeleteClose(); return }
    setDocs(prev => prev.filter(doc => doc._id !== deletingId))
    setDeletingId(null)
    onDeleteClose()
  }

  function viewDoc(doc: Doc) {
    setViewingDoc(doc)
    onViewOpen()
  }

  return (
    <Box minH="100vh" position="relative" p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>

      <Box maxW="700px" mx="auto">
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between" align="center">
            <Text fontSize="2xl" fontWeight="bold" color="white">📄 Documents</Text>
            {isAdmin && !showForm && (
              <Button size="sm" colorScheme="orange" onClick={openAdd}>Add Document</Button>
            )}
          </HStack>

          {isAdmin && showForm && (
            <Box {...glassCard} borderRadius="xl" p={{ base: 4, md: 6 }}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="whiteAlpha.700" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Title</FormLabel>
                  <Input ref={titleRef} value={formTitle} onChange={e => setFormTitle(e.target.value)}
                    placeholder="e.g. Score Sheet 2024" color="white" borderColor="whiteAlpha.300"
                    _placeholder={{ color: 'whiteAlpha.400' }} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="whiteAlpha.700" fontSize="xs" textTransform="uppercase" letterSpacing="wide">URL</FormLabel>
                  <Input value={formUrl} onChange={e => setFormUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..." color="white" borderColor="whiteAlpha.300"
                    _placeholder={{ color: 'whiteAlpha.400' }} />
                </FormControl>
                <FormControl>
                  <FormLabel color="whiteAlpha.700" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Description</FormLabel>
                  <Textarea value={formDescription} onChange={e => setFormDescription(e.target.value)}
                    placeholder="Optional description…" color="white" borderColor="whiteAlpha.300"
                    _placeholder={{ color: 'whiteAlpha.400' }} rows={2} />
                </FormControl>
                <HStack justify="flex-end" w="full">
                  <Button size="sm" variant="ghost" color="whiteAlpha.700" onClick={cancelForm}>Cancel</Button>
                  <Button size="sm" colorScheme="orange" onClick={save} isLoading={saving}
                    isDisabled={!formTitle.trim() || !formUrl.trim()}>
                    {editingDoc ? 'Save' : 'Add'}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {loading ? (
            <Spinner color="white" />
          ) : docs.length === 0 ? (
            <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">No documents yet.</Text>
          ) : (
            <VStack spacing={3} align="stretch">
              {docs.map(doc => (
                <Box key={doc._id} {...glassCard} borderRadius="lg" p={4}>
                  <HStack justify="space-between" align="start">
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <Text color="white" fontWeight="semibold" fontSize="sm" noOfLines={1}>{doc.title}</Text>
                      {doc.description && (
                        <Text color="whiteAlpha.500" fontSize="xs" mt={0.5}>{doc.description}</Text>
                      )}
                      <Link href={doc.url} isExternal color="whiteAlpha.400" fontSize="xs" mt={1}
                        noOfLines={1} _hover={{ color: 'orange.300' }}>
                        {doc.url}
                      </Link>
                    </VStack>
                    <HStack spacing={1} flexShrink={0} ml={2}>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => viewDoc(doc)}>
                        View
                      </Button>
                      {isAdmin && (
                        <>
                          <IconButton aria-label="Edit" icon={<Text fontSize="xs">✏️</Text>} size="xs"
                            variant="ghost" color="whiteAlpha.400" _hover={{ color: 'white' }}
                            onClick={() => openEdit(doc)} />
                          <IconButton aria-label="Delete" icon={<Text fontSize="xs">🗑️</Text>} size="xs"
                            variant="ghost" color="whiteAlpha.400" _hover={{ color: 'red.300' }}
                            onClick={() => confirmDelete(doc._id)} />
                        </>
                      )}
                    </HStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </VStack>
      </Box>

      {/* Full-screen viewer */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="full">
        <ModalOverlay />
        <ModalContent bg="gray.900" m={0} borderRadius={0} display="flex" flexDir="column">
          <ModalHeader color="white" borderBottomWidth="1px" borderColor="whiteAlpha.200" py={3}>
            {viewingDoc?.title}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody p={0} flex={1} display="flex" flexDir="column">
            {viewingDoc && (
              <iframe
                src={toEmbedUrl(viewingDoc.url)}
                style={{ flex: 1, width: '100%', height: '100%', border: 'none', minHeight: 'calc(100vh - 60px)' }}
                title={viewingDoc.title}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete confirmation */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Document</AlertDialogHeader>
            <AlertDialogBody>Are you sure? This cannot be undone.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" color="whiteAlpha.700" onClick={onDeleteClose}>Cancel</Button>
              <Button colorScheme="red" ml={3} onClick={deleteDoc}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
